import React, { useState, useMemo } from "react";
import type { MenuItem } from "../../data/menuData";
import type { AdminData } from "./AdminLayout";

interface Props {
  data: AdminData;
  updateData: (updater: (prev: AdminData) => AdminData) => void;
}

interface ItemForm {
  id: string;
  recipeId: string;
  name: string;
  nameFr: string;
  description: string;
  descriptionFr: string;
  price: string;
  category: string;
  location: string;
  submenu: string;
  popular: boolean;
  spicy: boolean;
  vegetarian: boolean;
  vegan: boolean;
  glutenFree: boolean;
  new: boolean;
}

const emptyForm: ItemForm = {
  id: "",
  recipeId: "",
  name: "",
  nameFr: "",
  description: "",
  descriptionFr: "",
  price: "",
  category: "lunch",
  location: "montreal",
  submenu: "",
  popular: false,
  spicy: false,
  vegetarian: false,
  vegan: false,
  glutenFree: false,
  new: false,
};

const CATEGORIES = ["lunch", "dinner", "specials", "new-arrivals", "grocery"];
const LOCATIONS = ["montreal", "delson"];

const MenuItemsManager: React.FC<Props> = ({ data, updateData }) => {
  const [filterLocation, setFilterLocation] = useState<string>("all");
  const [filterSubmenu, setFilterSubmenu] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<"add" | "edit" | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ItemForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Get available submenus based on filters
  const availableSubmenus = useMemo(() => {
    const subs = new Set<string>();
    for (const item of data.menuItems) {
      if (filterLocation !== "all" && item.location !== filterLocation)
        continue;
      if (item.submenu) subs.add(item.submenu);
    }
    return Array.from(subs).sort();
  }, [data.menuItems, filterLocation]);

  // All submenus from config for the form dropdown
  const allSubmenus = useMemo(() => {
    const subs = new Set<string>();
    for (const loc of Object.values(data.menuConfigs)) {
      for (const section of Object.values(loc)) {
        for (const sub of section.submenus) {
          subs.add(sub as string);
        }
      }
    }
    return Array.from(subs).sort();
  }, [data.menuConfigs]);

  const filteredItems = useMemo(() => {
    return data.menuItems.filter((item) => {
      if (filterLocation !== "all" && item.location !== filterLocation)
        return false;
      if (filterSubmenu !== "all" && item.submenu !== filterSubmenu)
        return false;
      if (search) {
        const q = search.toLowerCase();
        const recipe = item.recipeId ? data.recipes[item.recipeId] : null;
        const name = (
          item.name ||
          recipe?.name ||
          item.id
        ).toLowerCase();
        if (!name.includes(q) && !item.id.toLowerCase().includes(q))
          return false;
      }
      return true;
    });
  }, [data.menuItems, data.recipes, filterLocation, filterSubmenu, search]);

  const getItemName = (item: MenuItem): string => {
    if (item.name) return item.name;
    if (item.recipeId && data.recipes[item.recipeId]) {
      return data.recipes[item.recipeId].name;
    }
    return item.id;
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setModal("add");
  };

  const openEdit = (item: MenuItem) => {
    setForm({
      id: item.id,
      recipeId: item.recipeId || "",
      name: item.name || "",
      nameFr: item.nameFr || "",
      description: item.description || "",
      descriptionFr: item.descriptionFr || "",
      price: item.price.toString(),
      category: item.category,
      location: item.location,
      submenu: item.submenu || "",
      popular: item.popular || false,
      spicy: item.spicy || false,
      vegetarian: item.vegetarian || false,
      vegan: item.vegan || false,
      glutenFree: item.glutenFree || false,
      new: item.new || false,
    });
    setEditingId(item.id);
    setModal("edit");
  };

  const handleSave = () => {
    const price = parseFloat(form.price);
    if (!form.id || isNaN(price) || price <= 0) return;

    const newItem: MenuItem = {
      id: form.id,
      price,
      category: form.category as MenuItem["category"],
      location: form.location as MenuItem["location"],
    };

    if (form.recipeId) newItem.recipeId = form.recipeId;
    if (form.name) newItem.name = form.name;
    if (form.nameFr) newItem.nameFr = form.nameFr;
    if (form.description) newItem.description = form.description;
    if (form.descriptionFr) newItem.descriptionFr = form.descriptionFr;
    if (form.submenu) newItem.submenu = form.submenu;
    if (form.popular) newItem.popular = true;
    if (form.spicy) newItem.spicy = true;
    if (form.vegetarian) newItem.vegetarian = true;
    if (form.vegan) newItem.vegan = true;
    if (form.glutenFree) newItem.glutenFree = true;
    if (form.new) newItem.new = true;

    updateData((prev) => {
      const items = [...prev.menuItems];
      if (modal === "edit" && editingId) {
        const idx = items.findIndex((i) => i.id === editingId);
        if (idx !== -1) items[idx] = newItem;
      } else {
        if (items.some((i) => i.id === newItem.id)) {
          alert("An item with this ID already exists.");
          return prev;
        }
        items.push(newItem);
      }
      return { ...prev, menuItems: items };
    });

    setModal(null);
  };

  const handleDelete = (id: string) => {
    updateData((prev) => ({
      ...prev,
      menuItems: prev.menuItems.filter((i) => i.id !== id),
    }));
    setDeleteConfirm(null);
  };

  const getSubmenuLabel = (submenu: string): string => {
    const label = data.submenuLabels[submenu];
    return label ? label.en : submenu;
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Menu Items ({data.menuItems.length})</h2>
        <button className="btn btn-primary" onClick={openAdd}>
          + Add Item
        </button>
      </div>

      <div className="admin-toolbar">
        <select
          value={filterLocation}
          onChange={(e) => {
            setFilterLocation(e.target.value);
            setFilterSubmenu("all");
          }}
        >
          <option value="all">All Locations</option>
          {LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>
              {loc.charAt(0).toUpperCase() + loc.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={filterSubmenu}
          onChange={(e) => setFilterSubmenu(e.target.value)}
        >
          <option value="all">All Submenus</option>
          {availableSubmenus.map((sub) => (
            <option key={sub} value={sub}>
              {getSubmenuLabel(sub)}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="admin-item-count">
          {filteredItems.length} items shown
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Price</th>
              <th>Location</th>
              <th>Submenu</th>
              <th>Flags</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => (
              <tr key={item.id}>
                <td>
                  <strong>{getItemName(item)}</strong>
                  <br />
                  <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                    {item.id}
                  </span>
                </td>
                <td>${item.price.toFixed(2)}</td>
                <td>{item.location}</td>
                <td>
                  {item.submenu
                    ? getSubmenuLabel(item.submenu)
                    : "-"}
                </td>
                <td>
                  {item.popular && (
                    <span className="admin-badge popular">popular</span>
                  )}
                  {item.spicy && (
                    <span className="admin-badge spicy">spicy</span>
                  )}
                  {item.vegetarian && (
                    <span className="admin-badge vegetarian">veg</span>
                  )}
                  {item.new && <span className="admin-badge new">new</span>}
                </td>
                <td className="admin-table-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => openEdit(item)}
                  >
                    Edit
                  </button>
                  {deleteConfirm === item.id ? (
                    <>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        Confirm
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setDeleteConfirm(item.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modal === "add" ? "Add Menu Item" : "Edit Menu Item"}</h3>

            <div className="admin-field">
              <label>Item ID</label>
              <input
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                disabled={modal === "edit"}
                placeholder="e.g. montreal-lunch-new-dish"
              />
            </div>

            <div className="admin-field">
              <label>Recipe (optional - links to shared recipe data)</label>
              <select
                value={form.recipeId}
                onChange={(e) => setForm({ ...form, recipeId: e.target.value })}
              >
                <option value="">No recipe (use custom name below)</option>
                {Object.entries(data.recipes)
                  .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                  .map(([id, r]) => (
                    <option key={id} value={id}>
                      {r.name} / {r.nameFr}
                    </option>
                  ))}
              </select>
            </div>

            {!form.recipeId && (
              <>
                <div className="admin-field-row">
                  <div className="admin-field">
                    <label>Name (EN)</label>
                    <input
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="admin-field">
                    <label>Name (FR)</label>
                    <input
                      value={form.nameFr}
                      onChange={(e) =>
                        setForm({ ...form, nameFr: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="admin-field-row">
                  <div className="admin-field">
                    <label>Description (EN)</label>
                    <textarea
                      value={form.description}
                      onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                      }
                    />
                  </div>
                  <div className="admin-field">
                    <label>Description (FR)</label>
                    <textarea
                      value={form.descriptionFr}
                      onChange={(e) =>
                        setForm({ ...form, descriptionFr: e.target.value })
                      }
                    />
                  </div>
                </div>
              </>
            )}

            <div className="admin-field-row">
              <div className="admin-field">
                <label>Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
              <div className="admin-field">
                <label>Category</label>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-field-row">
              <div className="admin-field">
                <label>Location</label>
                <select
                  value={form.location}
                  onChange={(e) =>
                    setForm({ ...form, location: e.target.value })
                  }
                >
                  {LOCATIONS.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc.charAt(0).toUpperCase() + loc.slice(1)}
                    </option>
                  ))}
                  <option value="both">Both</option>
                </select>
              </div>
              <div className="admin-field">
                <label>Submenu</label>
                <select
                  value={form.submenu}
                  onChange={(e) =>
                    setForm({ ...form, submenu: e.target.value })
                  }
                >
                  <option value="">None</option>
                  {allSubmenus.map((sub) => (
                    <option key={sub} value={sub}>
                      {getSubmenuLabel(sub)} ({sub})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-field">
              <label>Flags</label>
              <div className="admin-checkbox-group">
                {(
                  [
                    ["popular", "Popular"],
                    ["spicy", "Spicy"],
                    ["vegetarian", "Vegetarian"],
                    ["vegan", "Vegan"],
                    ["glutenFree", "Gluten Free"],
                    ["new", "New"],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={form[key]}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.checked })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {modal === "add" ? "Add Item" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuItemsManager;
